// Giao diện theo mùa: dịp do admin chọn (GET /public/season, đã quy theo ngày VN) ghi lên
// <html data-season="…"> để CSS (styles/season.css) và các thành phần components/season/* đọc.
//   · Người dùng tự tắt hiệu ứng động trên máy mình (nhớ theo trình duyệt) — màu, trang trí vẫn giữ.
//   · Máy bật Giảm chuyển động → hiệu ứng đứng yên, như khi tắt.
//   · ?season=<dịp> trên URL: xem thử một dịp trong phiên này (?season=auto để bỏ).
//   · Hỏi lại máy chủ mỗi phút khi tab đang mở và ngay khi quay lại tab. Admin đổi dịp thì KHÔNG
//     tự đổi giữa chừng (như bản cập nhật PWA, lib/pwa.ts): hiện thông báo kèm nút Tải lại.

import { useSyncExternalStore } from 'react'
import { toast } from 'sonner'
import { SEASON_KEYS, SEASON_LABEL, type SeasonKey } from '@shoptrack/common/season'
import { api } from '@/lib/api'

const FX_KEY = 'st-season-fx'
const PREVIEW_KEY = 'st-season-preview'
const POLL_MS = 60_000
const MIN_GAP_MS = 15_000

export interface SeasonState {
  /** Dịp đang hiện; null = giao diện mặc định. */
  season: SeasonKey | null
  /** Người dùng cho phép hiệu ứng động. */
  fx: boolean
  /** Máy bật Giảm chuyển động. */
  reduced: boolean
}

const isKey = (v: unknown): v is SeasonKey => typeof v === 'string' && (SEASON_KEYS as readonly string[]).includes(v)

function readFx(): boolean {
  try {
    return window.localStorage.getItem(FX_KEY) !== 'off'
  } catch {
    return true
  }
}

/** Dịp xem thử: undefined = không xem thử, null = xem thử giao diện mặc định. */
function readPreview(): SeasonKey | null | undefined {
  try {
    const q = new URLSearchParams(window.location.search).get('season')
    if (q !== null) {
      if (q === 'auto' || q === '') window.sessionStorage.removeItem(PREVIEW_KEY)
      else if (q === 'off' || isKey(q)) window.sessionStorage.setItem(PREVIEW_KEY, q)
    }
    const v = window.sessionStorage.getItem(PREVIEW_KEY)
    return v === 'off' ? null : isKey(v) ? v : undefined
  } catch {
    return undefined
  }
}

const reducedQuery = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null

let state: SeasonState = { season: null, fx: typeof window === 'undefined' ? true : readFx(), reduced: !!reducedQuery?.matches }
const listeners = new Set<() => void>()

function emit(next: Partial<SeasonState>) {
  state = { ...state, ...next }
  const root = document.documentElement
  if (state.season) root.dataset.season = state.season
  else delete root.dataset.season
  // CSS dừng các chuyển động trang trí khi người dùng tắt hiệu ứng / máy bật Giảm chuyển động
  root.toggleAttribute('data-season-still', !seasonMotion(state))
  for (const l of listeners) l()
}

reducedQuery?.addEventListener?.('change', (e) => emit({ reduced: e.matches }))

/** Tải lại trang với cài đặt của admin (bỏ chế độ xem thử ?season nếu đang dùng). */
function reloadFresh() {
  try {
    window.sessionStorage.removeItem(PREVIEW_KEY)
  } catch {
    /* bỏ qua */
  }
  const url = new URL(window.location.href)
  url.searchParams.delete('season')
  window.location.replace(url.toString())
}

function notifyChanged(next: SeasonKey | null) {
  toast('Có cập nhật mới', {
    id: 'season-update',
    description: `Giao diện vừa đổi sang ${next ? SEASON_LABEL[next] : 'mặc định'}.`,
    duration: Infinity,
    action: { label: 'Tải lại', onClick: reloadFresh },
  })
}

let started = false
/** Gọi một lần lúc khởi động app. */
export function startSeason() {
  if (started || typeof window === 'undefined') return
  started = true
  const preview = readPreview()
  if (preview !== undefined) emit({ season: preview })

  // Dịp máy chủ báo lần gần nhất (undefined = chưa tải lần nào)
  let server: SeasonKey | null | undefined
  let lastAt = 0
  const load = () => {
    lastAt = Date.now()
    return api<{ season: string | null }>('/public/season', { cache: 'no-store' })
      .then((r) => {
        const next = isKey(r.season) ? r.season : null
        if (server === undefined) {
          server = next
          if (preview === undefined) emit({ season: next })
        } else if (next !== server) {
          server = next
          notifyChanged(next)
        }
      })
      .catch(() => {
        /* không tải được thì giữ giao diện đang có, lần sau hỏi lại */
      })
  }
  void load()
  window.setInterval(() => {
    if (!document.hidden) void load()
  }, POLL_MS)
  const onBack = () => {
    if (!document.hidden && Date.now() - lastAt > MIN_GAP_MS) void load()
  }
  document.addEventListener('visibilitychange', onBack)
  window.addEventListener('focus', onBack)
}

export function getSeasonState(): SeasonState {
  return state
}

export function subscribeSeason(fn: () => void) {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

export function useSeason(): SeasonState {
  return useSyncExternalStore(subscribeSeason, getSeasonState, getSeasonState)
}

/** Hiệu ứng có được chạy không (người dùng cho phép và máy không bật Giảm chuyển động). */
export function seasonMotion(s: SeasonState = state): boolean {
  return s.fx && !s.reduced
}

export function setSeasonFx(on: boolean) {
  try {
    if (on) window.localStorage.removeItem(FX_KEY)
    else window.localStorage.setItem(FX_KEY, 'off')
  } catch {
    /* trình duyệt chặn lưu — vẫn đổi trong phiên này */
  }
  emit({ fx: on })
}

const CELEBRATE = 'st:season-celebrate'
/** Gọi khi tạo đơn thành công — lớp hiệu ứng (SeasonLayer) bung hiệu ứng của dịp. */
export function celebrateSeason() {
  if (state.season) window.dispatchEvent(new Event(CELEBRATE))
}
export function onSeasonCelebrate(fn: () => void) {
  window.addEventListener(CELEBRATE, fn)
  return () => window.removeEventListener(CELEBRATE, fn)
}
